import { Button, Dialog, DialogContent, DialogTitle, Grid, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { makeStyles } from '@mui/styles';
import React, { useEffect, useState } from 'react';
import NumericKeypad from './numeric-keypad';
import { AddUpdateUserPinCommand, RwContact, UsersClient } from '../../api/web-api-client';
import classNames from 'classnames';
import clientService from '../../services/clientService';
import useRwSWR from '../../hooks/use-rw-swr';
import { useAppContext } from '../../context/app-context';
import Keys from '../../services/keys';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';

const useStyles = makeStyles((theme) => ({
    dialog: {
        minHeight: '270px',
        marginTop: '-20px',
    },
    dialogWithPin: {
        minHeight: '50px',
        marginTop: '-20px',
    },
    dialogTitle: {
        paddingTop: '0px',
    },
    dialogSubTitle: {
        paddingLeft: '48px', marginBottom: '20px', minHeight: '25px', marginRight: '50px'
    },
    buttons: {
        display: 'flex',
        justifyContent: 'flex-end',
        '& button': {
            marginLeft: '15px',
        },

    },
    overlay: {
        background: '#ffffff',
        height: '100%',
        width: '100%',
        textAlign: 'center',
        paddingTop: '20%',
        opacity: 0.70
    },
    yourPin: {
        marginBottom: '40px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
    },
    pin: {
        marginTop: '20px',
        fontWeight: 500,
        fontSize: '40px',
        lineHeight: '48px',
        textAlign: 'center',
        letterSpacing: '40px',
        paddingLeft: '40px',
    },
    yourPinMessage: {
        textAlign: 'center',
        marginTop: '40px',
    }

}));

export interface PinDialogProps {
    contact?: RwContact;
    open: boolean;
    handleClose: () => void;
    handlePinContinueToForms: () => void;
    title: string;
    subtitle: string;
    showCloseIcon?: boolean;
    submitText?: string;
    cancelText?: string;
}

interface IPinDialogViewModel {
    validMessage: string;
    inValidMessage: string;
    tooShortMessage: string;
    confirmMessage: string;
    title: string;
    subTitle: string;
    securitySubTitle: string;
    message: string;
    enterCount: number;
    hasPin: boolean;
    pin: string;
    pinConfirm: string;
    pinUpdated: boolean;
    signOutUrl: string;
    showPin: boolean;
    valid: boolean;
}



const PinDialog: React.FC<PinDialogProps> = (props) => {
    const classes = useStyles();

    const { ShowError, loading, setLoading, unwrapperedDomain } = useAppContext();
    const client = clientService.getFormsClient(UsersClient, { apiPath: unwrapperedDomain });
    const { data } = useRwSWR([Keys.ApiUser, props.contact?.id], (url) => fetcher(url));

    const validMessage = 'Please enter the PIN you would like to use';
    const yourPinMessage = 'Your PIN for accessing additional patient forms.';
    const inValidMessage = 'Your PIN did not match. ' + validMessage;
    const tooShortMessage = 'Your PIN must be at least 4 characters';
    const confirmMessage = 'Please re-enter the PIN';
    const reEnterMessage = 'To ensure that only authorized office employees can access sensitive data, this will log you out of RevenueWell.';
    const additionalSecurityMessage = 'For additional security, do not save your log in credentials in your browser.';

    const defaultViewModel: IPinDialogViewModel = {
        validMessage: validMessage,
        inValidMessage: inValidMessage,
        tooShortMessage: tooShortMessage,
        confirmMessage: confirmMessage,
        title: props.title,
        subTitle: props.subtitle,
        securitySubTitle: additionalSecurityMessage,
        message: validMessage,
        enterCount: 0,
        hasPin: false,
        pin: '',
        pinConfirm: '',
        pinUpdated: false,
        signOutUrl: '',
        showPin: false,
        valid: true
    }

    const [vm, setVm] = useState(defaultViewModel as IPinDialogViewModel);

    const fetcher = async (url) => {
        try {
            const user = await client.get(null);
            return user;
        } catch (e) {
            ShowError('Problem occurred while loading the user.');
        } finally {
            setLoading(false);
        }
    }

    const handleClose = () => {
        let pinToSave = vm.pin !== '' ? vm.pin : '';

        if (pinToSave === '')
            pinToSave = data?.pin ? data?.pin.toString() : '';

        defaultViewModel.title = vm.title;
        defaultViewModel.subTitle = vm.subTitle;
        defaultViewModel.hasPin = pinToSave.toString() !== '';
        defaultViewModel.pin = pinToSave.toString();
        setVm(defaultViewModel);
        props.handleClose();
    }


    const changePinClick = () => {
        setVm({ ...vm, hasPin: false, pin: '', pinConfirm: '', enterCount: 0, message: validMessage, valid: true });
    }

    const continueToFormsClick = async () => {
        props.handlePinContinueToForms();
    }

    const handleEnter = (val: string) => {

        if (vm.enterCount % 2 === 0) {
            setVm({ ...vm, pin: val, pinConfirm: vm.pinConfirm, message: confirmMessage, enterCount: vm.enterCount + 1, valid: true });
        } else {
            setVm({ ...vm, pin: vm.pin, pinConfirm: val, enterCount: vm.enterCount + 1 });
            if (vm.pin.length === 4 && val.length === 4) {

                if (vm.pin !== val) {
                    setVm({ ...vm, hasPin: false, pin: '', pinConfirm: '', enterCount: 0, message: inValidMessage, valid: false });
                } else {
                    savePin(+vm.pin);
                }
            }
        }
    }

    const savePin = async (pin: number) => {
        try {
            setLoading(true);
            const command = new AddUpdateUserPinCommand();
            command.pin = pin;
            await client.addUpdateUserPin(command);
            setVm({ ...vm, hasPin: true, pin: pin.toString(), pinConfirm: '', pinUpdated: true });
        } catch (e) {
            ShowError('Problem occurred while saving your PIN.');
        } finally {
            setLoading(false);
        }

    }

    useEffect(() => {
        if (vm.hasPin) {
            setVm({ ...vm, title: props.contact?'Going to ' + props.contact.firstName + ' ' + props.contact.lastName + '’s Forms': '', subTitle: reEnterMessage });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vm.hasPin])


    useEffect(() => {
        if (data?.pin) {
            setVm({ ...vm, pin: data.pin.toString(), hasPin: data.pin.toString() !== '' });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data])


    return (<Dialog aria-labelledby="simple-dialog-title" open={props.open} maxWidth={vm.hasPin ? 'sm' : 'xs'} fullWidth={false}>
        {props.showCloseIcon &&
            <IconButton aria-label="close" component="span" sx={{ alignSelf: 'flex-end', marginRight: '15px', marginTop: '15px' }}
                onClick={handleClose}>
                <CloseIcon />
            </IconButton>
        }
        <Grid container>
            <Grid item xs={12}>
                <DialogTitle className={classes.dialogTitle} id="confirmation-dialog-title">{vm.title}</DialogTitle>
            </Grid>
            <Grid item xs={12} className={classes.dialogSubTitle}>
                <p>{vm.subTitle}</p>
                {vm.hasPin &&
                    <div className={classes.yourPin}>
                        <div><i>{additionalSecurityMessage}</i></div>

                        <div className={classes.yourPinMessage}>{yourPinMessage}</div>
                        <div className={classes.pin}>
                            {vm.showPin && <>{vm.pin}</>}
                            {!vm.showPin && <>....</>}
                        </div>
                    </div>
                }
            </Grid>
        </Grid>
        <DialogContent className={classNames(vm.hasPin ? classes.dialogWithPin : classes.dialog)}>
            {!loading && vm.hasPin &&
                <div className={classes.buttons}>

                    <Button variant="contained" size="small" sx={{ width: '115px' }}
                        onClick={() => { setVm({ ...vm, showPin: !vm.showPin }); }} color="secondary" startIcon={!vm.showPin ? <RemoveRedEyeOutlinedIcon /> : <VisibilityOffOutlinedIcon />}>{!vm.showPin ? 'Show PIN' : 'Hide PIN'}</Button>
                    <Button variant="contained" size="small"
                        onClick={changePinClick} color="secondary" startIcon={<EditOutlinedIcon />}>Change PIN</Button>
                    <Button variant="contained" size="small"
                        onClick={continueToFormsClick} endIcon={<ArrowForwardOutlinedIcon />}>Continue to Forms</Button>
                </div>
            }
            {!vm.hasPin &&
                <NumericKeypad handleEnter={handleEnter} validMessage={vm.message} tooShortMessage={tooShortMessage} valid={vm.valid} />
            }
        </DialogContent>
    </Dialog>


    );
}

export default PinDialog